import axios, { AxiosResponse } from "axios";
import { WebAPI, IFailCallbackParam } from "../constants";
import { getErrorStatus } from "./getErrorStatus";
import AuthService from "../../common/AuthService";
import { ControlMode, SettingType, AnyType } from "../../common/constants";

interface ICallbackParams extends IFailCallbackParam {
  /**
   * WebAPIでモデル学習開始が成功した場合に呼び出されます
   * @callback success
   * @param {number} status - httpステータス
   */
  success: (status: number) => void;
}

export interface ITrainModelAutoDirectTypeRequestModel {
  /**
   * Rev番号
   */
  version: string;
  /**
   * 学習条件
   */
  learnmode: string;
  /**
   * 制御モード
   */
  controlmode?: string;
  /**
   * PID制御データのファイルID
   */
  piddatafileid?: string;
  /**
   * 使用するシミュレータID
   */
  simulatorid?: string;
  /**
   * Moonshot制御周期
   */
  fkdppinterval?: number;
  /**
   * ステップ数
   */
  exp_time?: number
  /**
   * 目標値
   */
  sp?: number;
}

export interface ITrainModelAutoIndirectTypeRequestModel {
  /**
   * Rev番号
   */
  version: string;
  /**
   * 学習条件
   */
  learnmode: string;
  /**
   * 制御モード
   */
  controlmode?: string;
  /**
   * PID制御データのファイルID
   */
  piddatafileid?: string;
  /**
   * 使用するシミュレータID
   */
  simulatorid?: string;
  /**
   * ステップ数
   */
  exp_time?: number
  /**
   * 目標値
   */
  sp?: number;
  /**
   * 比例帯
   */
  pb?: number;
  /**
   * 積分時間
   */
  ti?: number;
  /**
   * 微分時間
   */
  td?: number;
  /**
   * PID制御周期
   */
  pidinterval?: number;
  /**
   * 制御レンジ上限
   */
  rangemax?: number;
  /**
   * 制御レンジ下限
   */
  rangemin?: number;
}

export interface ITrainModelCustomTypeRequestModel {
  /**
   * Rev番号
   */
  version: string;
  /**
   * 学習条件
   */
  learnmode: string;
}

function AxiosPost(
  url: string,
  params: AnyType,
  token?: string
) {
  if (token) {
    return axios.post(url, JSON.stringify(params), {
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
    });
  } else {
    return axios.post(url, JSON.stringify(params), {
      headers: { "Content-Type": "application/json" },
    });
  }
}

interface IAxiosInternalParams extends ICallbackParams {
  promise: Promise<AxiosResponse>;
}

function AxiosInternal(params: IAxiosInternalParams) {
  params.promise
    .then((res) => {
      params.success(res.status);
    })
    .catch((err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    });
}

export interface ITrainModelParams
  extends ITrainModelAutoDirectTypeRequestModel, ITrainModelAutoIndirectTypeRequestModel,
    ITrainModelCustomTypeRequestModel, ICallbackParams {
  /**
   * 認証オブジェクト
   */
  authService?: AuthService;
  /**
   * プロジェクトID
   */
  projectid: string;
  /**
   * 学習条件と制御モードの種類
   */
  type: string;
}

/**
 * モデル学習を開始します
 * @function trainModel
 * @param {ITrainModelParams} params - モデル学習開始パラメータ
 */
export function trainModel(params: ITrainModelParams): void {
  const type_auto = SettingType.AUTO;
  const type_custom = SettingType.CUSTOM;
  const direct = ControlMode.DIRECT;
  const indirect = ControlMode.INDIRECT;
  
  const { 
    authService, 
    projectid,
    type,
    version, 
    learnmode, 
    controlmode, 
    piddatafileid,
    simulatorid,
    fkdppinterval,
    sp,
    exp_time,
    pb,
    ti,
    td,
    pidinterval,
    rangemax,
    rangemin
  } = params;

  let req: AnyType;
  
  if (type === type_auto + direct) {  
    const reqAutoDirect: ITrainModelAutoDirectTypeRequestModel = {
      version: version,
      learnmode: learnmode,
      controlmode: controlmode,
      piddatafileid: piddatafileid,
      simulatorid: simulatorid,
      fkdppinterval: fkdppinterval,
      sp: sp,
      exp_time: exp_time,
    };
     req = reqAutoDirect;  
  } else if (type === type_auto + indirect) {
    const reqAutoIndirect: ITrainModelAutoIndirectTypeRequestModel = {
      version: version,
      learnmode: learnmode,
      controlmode: controlmode,
      piddatafileid: piddatafileid,
      simulatorid: simulatorid,
      sp: sp,
      exp_time: exp_time,
      pb: pb,
      ti: ti,
      td: td,
      pidinterval: pidinterval,
      rangemax: rangemax,
      rangemin: rangemin,
    };
    req = reqAutoIndirect;
  } else if (type === type_custom) {
    const reqCustom: ITrainModelCustomTypeRequestModel = {
      version: version,
      learnmode: learnmode,
    };
    req = reqCustom
  }

  const url = WebAPI.TRAIN_MODEL(projectid);
  if (authService) {
    authService.getToken({
      success: (token) => {
        AxiosInternal({
          promise: AxiosPost(url, req, token),
          success: params.success,
          fail: (id, status) => {
            if (status === 401) {
              trainModelRetry401(authService, url, req, params);
            } else {
              params.fail(id, status);
            }
          },
        });
      },
      fail: (err) => {
        const { id, status } = getErrorStatus(err);
        params.fail(id, status);
      },
    });
  } else {
    AxiosInternal({
      promise: AxiosPost(url, req),
      success: params.success,
      fail: params.fail,
    });
  }
}

/**
 * モデル学習を開始します
 * @function trainModelRetry401
 * @param {AuthService} authService - 認証オブジェクト
 * @param {string} url - リクエストURL
 * @param {AnyType} req - モデル学習開始パラメータ
 * @param {ICallbackParams} params - コールバックパラメータ
 */
function trainModelRetry401(
  authService: AuthService,
  url: string,
  req: AnyType,
  params: ICallbackParams
): void {
  authService.updateToken({
    success: (token) => {
      AxiosInternal({
        promise: AxiosPost(url, req, token),
        success: params.success,
        fail: params.fail,
      })
    },
    fail: (err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    },
  });
}